import { UserRound } from 'lucide-react'

import {
  EditActions,
  EditableField,
  EditableSubfield,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { Input } from '@/customer/components/ui/input'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useUpdateMe } from '@/customer/hooks/use-update-me'

/**
 * Portal name field.
 *
 * First name + last name as a single row with inline editing. Names
 * live on the `members` row and are updated via `PATCH /portal/me`;
 * TanStack Query invalidation of the `me` key on save keeps the
 * displayed value fresh without a manual refetch.
 */
export function NameProfileField() {
  const me = useGetMe()
  const updateMe = useUpdateMe()

  const savedFirst = me.data?.member.firstName ?? ''
  const savedLast = me.data?.member.lastName ?? ''

  const nameField = useEditableField({
    initialValue: { first: savedFirst, last: savedLast },
    onSave: async ({ value, setError }) => {
      const nextFirst = value.first.trim()
      const nextLast = value.last.trim()
      if (nextFirst === savedFirst && nextLast === savedLast) return
      try {
        await updateMe.mutateAsync({ firstName: nextFirst, lastName: nextLast })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong.')
      }
    },
  })

  if (!me.data) return null

  const displayValue =
    savedFirst || savedLast ? (
      `${savedFirst} ${savedLast}`.trim()
    ) : (
      <span className="text-muted-foreground">Not set</span>
    )

  return (
    <EditableField
      label="Name"
      icon={UserRound}
      error={nameField.error}
      action={<EditButton field={nameField} label="name" />}
    >
      {nameField.isEditing ? (
        <div className="flex flex-col gap-2">
          <EditableSubfield label="First name">
            <Input
              autoFocus
              variant="underline"
              value={nameField.draft.first}
              onChange={(e) => nameField.setDraft({ ...nameField.draft, first: e.target.value })}
              placeholder="First name"
              autoComplete="given-name"
            />
          </EditableSubfield>
          <EditableSubfield label="Last name">
            <Input
              variant="underline"
              value={nameField.draft.last}
              onChange={(e) => nameField.setDraft({ ...nameField.draft, last: e.target.value })}
              placeholder="Last name"
              autoComplete="family-name"
            />
          </EditableSubfield>
        </div>
      ) : (
        <EditableValue>{displayValue}</EditableValue>
      )}
      <EditActions field={nameField} />
    </EditableField>
  )
}
